<template>
  <div class="fp"
       ref="container">
    <svg class="fp__svg"
         ref="svg"
         v-on:pointercancel="onPointerUp"
         v-on:pointerdown="onPointerDown"
         v-on:pointermove="onPointerMove"
         v-on:pointerup="onPointerUp"
         v-on:wheel.prevent="onWheel">
      <g v-bind:transform="transform">
        <!-- Hallenplatte -->
        <polygon v-bind:class="[ 'fp-slab__side', `fp-slab__side--${side.shade}` ]"
                 v-bind:key="`slab-${index}`"
                 v-bind:points="side.points"
                 v-for="(side, index) in scene.slab.sides" />
        <polygon class="fp-slab__top"
                 v-bind:points="scene.slab.top" />

        <!-- Bodenschatten -->
        <polygon class="fp-shadow"
                 v-bind:key="`shadow-${index}`"
                 v-bind:points="shadow"
                 v-for="(shadow, index) in shadows" />

        <!-- Blöcke (Wände, Stände, Flächen) in Zeichenreihenfolge -->
        <g v-bind:aria-label="ariaLabel(block)"
           v-bind:class="blockClasses(block)"
           v-bind:data-number="block.number || null"
           v-bind:key="block.key"
           v-bind:role="isInteractive(block) ? 'link' : null"
           v-bind:tabindex="isInteractive(block) ? 0 : null"
           v-for="block in scene.blocks"
           v-on:keydown.enter.prevent="activate(block.number)"
           v-on:keydown.space.prevent="activate(block.number)"
           v-on:mouseenter="onEnter(block)"
           v-on:mouseleave="onLeave">
          <polygon v-bind:class="[ 'fp-face', `fp-face--${side.shade}` ]"
                   v-bind:key="`side-${index}`"
                   v-bind:points="side.points"
                   v-for="(side, index) in block.sides" />
          <polygon class="fp-face fp-face--top"
                   v-bind:key="`top-${index}`"
                   v-bind:points="top"
                   v-for="(top, index) in block.tops" />
          <text class="fp-number"
                dominant-baseline="central"
                text-anchor="middle"
                v-bind:x="block.anchor[0]"
                v-bind:y="block.anchor[1]"
                v-if="block.type === 'stand' && block.label == null && !hasSign(block)">{{ block.number }}</text>
          <text class="fp-label"
                dominant-baseline="central"
                text-anchor="middle"
                v-bind:y="block.anchor[1] + (hasSign(block) ? 10 : 0)"
                v-else-if="block.type !== 'wall' && (block.type !== 'stand' || !hasSign(block)) && labelLines(block).length > 0">
            <tspan v-bind:class="{ 'fp-label__number': index === 0 && block.number != null && !hasSign(block) }"
                   v-bind:dy="index === 0 ? `${-(labelLines(block).length - 1) * 0.6}em` : '1.2em'"
                   v-bind:key="index"
                   v-bind:x="block.anchor[0]"
                   v-for="(line, index) in labelLines(block)">{{ line }}</tspan>
          </text>
        </g>

        <!-- Hervorhebung (Suche / Auswahl) -->
        <g class="fp-highlight"
           v-if="highlightBlock != null">
          <polygon v-bind:key="`hl-${index}`"
                   v-bind:points="top"
                   v-for="(top, index) in highlightBlock.tops" />
        </g>

        <!-- Eingang -->
        <g class="fp-entrance"
           v-if="scene.entrance != null">
          <line v-bind:x1="scene.entrance.gap[0]"
                v-bind:x2="scene.entrance.label[0]"
                v-bind:y1="scene.entrance.gap[1]"
                v-bind:y2="scene.entrance.label[1] - 5" />
          <rect height="10"
                rx="5"
                width="34"
                v-bind:x="scene.entrance.label[0] - 17"
                v-bind:y="scene.entrance.label[1] - 5" />
          <text dominant-baseline="central"
                text-anchor="middle"
                v-bind:x="scene.entrance.label[0]"
                v-bind:y="scene.entrance.label[1]">{{ $t('entrance') }}</text>
        </g>

        <!-- Schilder mit Logo und Standnummer -->
        <g v-bind:class="[ 'fp-signs', signsVisible ? 'fp-signs--visible' : null ]">
          <g v-bind:class="[ 'fp-sign', sign.dark ? 'fp-sign--dark' : null ]"
             v-bind:data-number="sign.number"
             v-bind:key="sign.key"
             v-bind:transform="`translate(${sign.x}, ${sign.y})`"
             v-for="sign in signs"
             v-on:mouseenter="onEnter(sign.block)"
             v-on:mouseleave="onLeave">
            <rect class="fp-sign__card"
                  rx="1.5"
                  v-bind:height="sign.h"
                  v-bind:width="sign.w"
                  v-bind:x="-sign.w / 2"
                  v-bind:y="-sign.h" />
            <image preserveAspectRatio="xMidYMid meet"
                   v-bind:height="sign.h - 4.5"
                   v-bind:href="sign.logoUrl"
                   v-bind:width="sign.w - 2"
                   v-bind:x="-sign.w / 2 + 1"
                   v-bind:y="-sign.h + 1"
                   v-if="sign.logoUrl != null && !failedLogos[sign.logoUrl]"
                   v-on:error="onLogoError(sign.logoUrl)" />
            <text class="fp-sign__name"
                  text-anchor="middle"
                  v-else>
              <tspan v-bind:key="index"
                     x="0"
                     v-bind:y="-sign.h + 4.2 + index * 3.4"
                     v-for="(line, index) in sign.nameLines">{{ line }}</tspan>
            </text>
            <rect class="fp-sign__badge"
                  height="4.4"
                  rx="2.2"
                  y="-2.2"
                  v-bind:width="sign.badgeWidth"
                  v-bind:x="-sign.badgeWidth / 2" />
            <text class="fp-sign__badge-text"
                  dominant-baseline="central"
                  text-anchor="middle"
                  x="0"
                  y="0">{{ sign.number }}</text>
          </g>
        </g>
      </g>
    </svg>

    <div class="fp__controls">
      <v-btn class="fp__control"
             icon
             small
             v-bind:title="$t('zoomIn')"
             v-on:click="zoomBy(1.5)">
        <v-icon small>ti-plus</v-icon>
      </v-btn>
      <v-btn class="fp__control"
             icon
             small
             v-bind:title="$t('zoomOut')"
             v-on:click="zoomBy(1 / 1.5)">
        <v-icon small>ti-minus</v-icon>
      </v-btn>
      <v-btn class="fp__control"
             icon
             small
             v-bind:title="$t('resetView')"
             v-on:click="fit(true)">
        <v-icon small>ti-focus-centered</v-icon>
      </v-btn>
    </div>

    <div class="fp__tooltip"
         v-bind:style="{ left: `${tooltip.x}px`, top: `${tooltip.y}px` }"
         v-if="tooltip != null">
      <div class="fp__tooltip-title">{{ tooltip.title }}</div>
      <div class="fp__tooltip-subtitle"
           v-if="tooltip.subtitle != null">{{ tooltip.subtitle }}</div>
    </div>
  </div>
</template>

<script>
  import { buildScene } from '@/lib/floorplan/iso'

  // Ab dieser Vergrößerung (Bildschirm-Pixel je Planeinheit) werden Logos statt Nummern gezeigt
  const SIGN_MIN_SCALE = 1.5
  const SIGN = { w: 22, h: 14 }
  const CLICK_TOLERANCE = 6
  const ANIMATION_MS = 350

  /**
   * Isometrischer Hallenplan als SVG mit Verschieben/Zoomen (Maus, Rad, Touch, Pinch).
   *
   * Props:
   *   plan         Plan-Modell (floorplan.json)
   *   assignments  Standnummer → { supplierId, name, logoUrl, darkBackground, productCount, conflict }
   *   mode         'customer' (nur belegte Stände klickbar) oder 'admin' (alle nummerierten Flächen klickbar)
   *   selected     dauerhaft hervorgehobene Standnummer (z. B. Auswahl im Admin)
   * Events:
   *   select { number, block, assignment }
   *   zoom   scale
   */
  export default {
    beforeDestroy() {
      if (this.resizeObserver != null) {
        this.resizeObserver.disconnect()
      } else {
        window.removeEventListener('resize', this.onResize)
      }

      this.cancelAnimation()
      window.clearTimeout(this.pulseTimer)
    },
    computed: {
      blocksByNumber() {
        const map = {}

        for (const block of this.scene.blocks) {
          if (block.number != null) {
            map[block.number] = block
          }
        }

        return map
      },
      highlightBlock() {
        const number = this.pulseNumber != null ? this.pulseNumber : this.selected

        return number != null ? this.blocksByNumber[number] || null : null
      },
      scene() {
        return buildScene(this.plan)
      },
      shadows() {
        return this.scene.blocks.flatMap(b => b.shadows)
      },
      signs() {
        const signs = []

        for (const block of this.scene.blocks) {
          if (!this.hasSign(block)) {
            continue
          }

          const assignment = this.assignments[block.number]
          const number = String(block.number)

          signs.push({
            key: `sign-${block.key}`,
            block,
            number,
            x: block.anchor[0],
            y: block.anchor[1],
            w: SIGN.w,
            h: SIGN.h,
            badgeWidth: 4 + number.length * 2.4,
            logoUrl: assignment.logoUrl,
            dark: assignment.logoUrl != null && this.isLightLogo(assignment.logoUrl, assignment.darkBackground),
            nameLines: this.wrap(assignment.name, 13, 2)
          })
        }

        // Vordere Schilder überdecken hintere
        return signs.sort((a, b) => a.y - b.y)
      },
      signsVisible() {
        return this.scale >= SIGN_MIN_SCALE
      },
      transform() {
        return `matrix(${this.scale}, 0, 0, ${this.scale}, ${this.tx}, ${this.ty})`
      }
    },
    created() {
      // Nicht reaktiv: Zeigerzustände und Animation
      this.pointers = new Map()
      this.analyzing = new Set()
      this.animation = null
      this.pressed = null
      this.moved = false
      this.canHover = typeof window !== 'undefined' && window.matchMedia != null && window.matchMedia('(hover: hover)').matches
    },
    data: () => ({
      failedLogos: {},
      logoTones: {},
      fitScale: 1,
      hoverNumber: null,
      pulseNumber: null,
      pulseTimer: null,
      scale: 1,
      tooltip: null,
      touched: false,
      tx: 0,
      ty: 0
    }),
    methods: {
      /**
       * Öffentlich: Stand zentrieren, heranzoomen und kurz hervorheben.
       */
      focusStand(number, { pulse = true } = {}) {
        const block = this.blocksByNumber[number]

        if (block == null) {
          return false
        }

        const { width, height } = this.containerSize()
        const scale = this.clampScale(Math.max(this.scale, 2.4))

        this.touched = true
        this.animateTo({
          scale,
          tx: width / 2 - block.anchor[0] * scale,
          ty: height / 2 - (block.anchor[1] - 6) * scale
        })

        if (pulse) {
          window.clearTimeout(this.pulseTimer)
          this.pulseNumber = number
          this.pulseTimer = window.setTimeout(() => {
            this.pulseNumber = null
          }, 2500)
        }

        return true
      },
      activate(number) {
        if (number == null) {
          return
        }

        const block = this.blocksByNumber[number]
        const assignment = this.assignments[number] || null

        if (block == null || !this.isInteractive(block)) {
          return
        }

        this.$emit('select', { number, block, assignment })
      },
      animateTo(target) {
        this.cancelAnimation()

        const from = { scale: this.scale, tx: this.tx, ty: this.ty }
        const start = performance.now()

        const step = now => {
          const t = Math.min(1, (now - start) / ANIMATION_MS)
          const e = 1 - Math.pow(1 - t, 3)

          this.scale = from.scale + (target.scale - from.scale) * e
          this.tx = from.tx + (target.tx - from.tx) * e
          this.ty = from.ty + (target.ty - from.ty) * e

          if (t < 1) {
            this.animation = window.requestAnimationFrame(step)
          } else {
            this.animation = null
            this.$emit('zoom', this.scale)
          }
        }

        this.animation = window.requestAnimationFrame(step)
      },
      ariaLabel(block) {
        if (block.number == null) {
          return null
        }

        const assignment = this.assignments[block.number]

        return assignment != null ? `${this.$t('stand')} ${block.number}, ${assignment.name}` : `${this.$t('stand')} ${block.number}, ${this.$t('free')}`
      },
      blockClasses(block) {
        const assignment = block.number != null ? this.assignments[block.number] : null

        return [
          'fp-block',
          `fp-block--${block.type}`,
          block.kind != null ? `fp-block--${block.kind}` : null,
          {
            'fp-block--assigned': assignment != null,
            'fp-block--conflict': this.mode === 'admin' && assignment != null && assignment.conflict,
            'fp-block--empty': block.type === 'stand' && assignment == null,
            'fp-block--hover': block.number != null && this.hoverNumber === block.number,
            'fp-block--interactive': this.isInteractive(block),
            'fp-block--selected': block.number != null && this.selected === block.number
          }
        ]
      },
      cancelAnimation() {
        if (this.animation != null) {
          window.cancelAnimationFrame(this.animation)
          this.animation = null
        }
      },
      clampScale(scale) {
        return Math.min(Math.max(scale, this.fitScale * 0.5), Math.max(this.fitScale * 8, 4))
      },
      containerSize() {
        const container = this.$refs.container

        return { width: container != null ? container.clientWidth : 0, height: container != null ? container.clientHeight : 0 }
      },
      /**
       * Öffentlich: gesamten Plan einpassen.
       */
      fit(animate = false) {
        const { width, height } = this.containerSize()
        const b = this.scene.bounds
        const bw = b.x1 - b.x0
        const bh = b.y1 - b.y0

        if (width === 0 || height === 0) {
          return
        }

        const scale = Math.min(width / bw, height / bh) * 0.94
        const target = {
          scale,
          tx: (width - bw * scale) / 2 - b.x0 * scale,
          ty: (height - bh * scale) / 2 - b.y0 * scale
        }

        this.fitScale = scale
        this.touched = false

        if (animate) {
          this.animateTo(target)
        } else {
          this.cancelAnimation()
          this.scale = target.scale
          this.tx = target.tx
          this.ty = target.ty
          this.$emit('zoom', this.scale)
        }
      },
      /**
       * Helle Logos (z. B. weiß auf transparent) bekommen ein dunkles Schild. Die Helligkeit wird einmal je
       * Logo über ein Canvas ermittelt; bis dahin (oder wenn das Bild nicht lesbar ist) gilt das Kennzeichen
       * „dunkler Hintergrund“ des Lieferanten.
       */
      isLightLogo(url, fallback) {
        const tone = this.logoTones[url]

        if (tone == null) {
          this.analyzeLogo(url)

          return !!fallback
        }

        return tone === 'light'
      },
      analyzeLogo(url) {
        if (this.analyzing.has(url)) {
          return
        }

        this.analyzing.add(url)

        const image = new Image()
        image.crossOrigin = 'anonymous'
        image.onload = () => {
          try {
            const size = 48
            const canvas = document.createElement('canvas')
            canvas.width = size
            canvas.height = size
            const context = canvas.getContext('2d')
            context.drawImage(image, 0, 0, size, size)
            const data = context.getImageData(0, 0, size, size).data
            let sum = 0
            let count = 0

            for (let i = 0; i < data.length; i += 4) {
              if (data[i + 3] < 128) {
                continue
              }

              sum += 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]
              count++
            }

            this.$set(this.logoTones, url, count > 0 && sum / count > 170 ? 'light' : 'dark')
          } catch (e) {
            this.$set(this.logoTones, url, 'unknown')
          }
        }
        image.onerror = () => this.$set(this.logoTones, url, 'unknown')
        image.src = url
      },
      hasSign(block) {
        return block.number != null && this.assignments[block.number] != null && this.signsVisible
      },
      isInteractive(block) {
        if (block.number == null || block.type === 'wall') {
          return false
        }

        return this.mode === 'admin' || this.assignments[block.number] != null
      },
      labelLines(block) {
        const lines = []

        // Mit Schild steht die Nummer bereits auf der Plakette
        if (block.number != null && !this.hasSign(block)) {
          lines.push(String(block.number))
        }

        if (block.label != null) {
          if (block.label.startsWith('SAGASSER ')) {
            lines.push('SAGASSER', block.label.substring('SAGASSER '.length))
          } else if (block.label.length > 12 && block.label.includes(' ')) {
            lines.push(...this.wrap(block.label, 12, 3))
          } else {
            lines.push(block.label)
          }
        }

        return lines
      },
      localPoint(event) {
        const rect = this.$refs.svg.getBoundingClientRect()

        return { x: event.clientX - rect.left, y: event.clientY - rect.top }
      },
      onEnter(block) {
        if (!this.canHover || block.number == null && block.label == null) {
          return
        }

        this.hoverNumber = block.number
        const assignment = block.number != null ? this.assignments[block.number] : null

        let title, subtitle = null

        if (assignment != null) {
          title = assignment.name
          subtitle = `${this.$t('stand')} ${block.number}`

          if (this.mode === 'admin' && assignment.conflict) {
            subtitle += ` · ${this.$t('conflict')}`
          } else if (this.mode === 'admin' && assignment.state !== 'accepted') {
            subtitle += ` · ${this.$t('notAccepted')}`
          }
        } else if (block.type === 'stand') {
          title = `${this.$t('stand')} ${block.number}`
          subtitle = this.$t('free')
        } else {
          title = block.label
          subtitle = block.number != null ? `${this.$t('stand')} ${block.number}` : null
        }

        this.tooltip = {
          title,
          subtitle,
          x: block.anchor[0] * this.scale + this.tx,
          y: (block.anchor[1] - (this.hasSign(block) ? SIGN.h : 2)) * this.scale + this.ty
        }
      },
      onLeave() {
        this.hoverNumber = null
        this.tooltip = null
      },
      onLogoError(url) {
        this.$set(this.failedLogos, url, true)
      },
      onPointerDown(event) {
        this.cancelAnimation()
        this.tooltip = null

        const point = this.localPoint(event)
        this.pointers.set(event.pointerId, point)

        if (this.pointers.size === 1) {
          this.moved = false
          this.pressStart = point
          const target = event.target.closest != null ? event.target.closest('[data-number]') : null
          this.pressed = target != null ? target.getAttribute('data-number') : null
        } else {
          this.pressed = null
        }

        if (this.pointers.size === 2) {
          const [a, b] = [...this.pointers.values()]
          this.pinch = { mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, dist: Math.hypot(a.x - b.x, a.y - b.y) }
        }

        this.last = point
      },
      onPointerMove(event) {
        if (!this.pointers.has(event.pointerId)) {
          return
        }

        const point = this.localPoint(event)
        this.pointers.set(event.pointerId, point)

        if (this.pointers.size === 1) {
          if (!this.moved && Math.hypot(point.x - this.pressStart.x, point.y - this.pressStart.y) > CLICK_TOLERANCE) {
            this.moved = true
          }

          if (this.moved) {
            this.touched = true
            this.tx += point.x - this.last.x
            this.ty += point.y - this.last.y
          }

          this.last = point
        } else if (this.pointers.size === 2) {
          const [a, b] = [...this.pointers.values()]
          const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
          const dist = Math.hypot(a.x - b.x, a.y - b.y)

          this.moved = true
          this.touched = true
          this.zoomAt(mid, dist / (this.pinch.dist || dist))
          this.tx += mid.x - this.pinch.mid.x
          this.ty += mid.y - this.pinch.mid.y
          this.pinch = { mid, dist }
        }
      },
      onPointerUp(event) {
        this.pointers.delete(event.pointerId)

        if (this.pointers.size === 0) {
          if (!this.moved && this.pressed != null && event.type === 'pointerup') {
            this.activate(this.pressed)
          }

          this.pressed = null
          this.$emit('zoom', this.scale)
        } else if (this.pointers.size === 1) {
          this.last = [...this.pointers.values()][0]
        }
      },
      onResize() {
        if (!this.touched) {
          this.fit(false)
        }
      },
      onWheel(event) {
        const delta = Math.max(-80, Math.min(80, event.deltaY))

        this.touched = true
        this.tooltip = null
        this.zoomAt(this.localPoint(event), Math.exp(-delta * 0.0025))
        this.$emit('zoom', this.scale)
      },
      wrap(text, maxLength, maxLines) {
        const words = String(text || '').split(/\s+/)
        const lines = []
        let current = ''

        for (const word of words) {
          if (current.length > 0 && (current + ' ' + word).length > maxLength) {
            lines.push(current)
            current = word
          } else {
            current = current.length > 0 ? `${current} ${word}` : word
          }
        }

        if (current.length > 0) {
          lines.push(current)
        }

        if (lines.length > maxLines) {
          const kept = lines.slice(0, maxLines)
          kept[maxLines - 1] = kept[maxLines - 1].substring(0, maxLength - 1) + '…'

          return kept
        }

        return lines.map(l => l.length > maxLength + 2 ? l.substring(0, maxLength + 1) + '…' : l)
      },
      zoomAt(point, factor) {
        const scale = this.clampScale(this.scale * factor)
        const k = scale / this.scale

        this.tx = point.x - (point.x - this.tx) * k
        this.ty = point.y - (point.y - this.ty) * k
        this.scale = scale
      },
      zoomBy(factor) {
        const { width, height } = this.containerSize()
        const point = { x: width / 2, y: height / 2 }
        const scale = this.clampScale(this.scale * factor)
        const k = scale / this.scale

        this.touched = true
        this.animateTo({
          scale,
          tx: point.x - (point.x - this.tx) * k,
          ty: point.y - (point.y - this.ty) * k
        })
      }
    },
    mounted() {
      this.fit(false)

      if (window.ResizeObserver != null) {
        this.resizeObserver = new window.ResizeObserver(() => this.onResize())
        this.resizeObserver.observe(this.$refs.container)
      } else {
        window.addEventListener('resize', this.onResize)
      }
    },
    name: 'FloorPlan',
    props: {
      assignments: {
        default: () => ({}),
        type: Object
      },
      mode: {
        default: 'customer',
        type: String
      },
      plan: {
        required: true,
        type: Object
      },
      selected: {
        default: null,
        type: String
      }
    }
  }
</script>

<i18n>
  {
    "de": {
      "conflict": "Mehrfach belegt",
      "entrance": "Eingang",
      "free": "Frei",
      "notAccepted": "Noch nicht angenommen",
      "resetView": "Ansicht zurücksetzen",
      "stand": "Stand",
      "zoomIn": "Vergrößern",
      "zoomOut": "Verkleinern"
    },
    "en": {
      "conflict": "Assigned multiple times",
      "entrance": "Entrance",
      "free": "Free",
      "notAccepted": "Not accepted yet",
      "resetView": "Reset view",
      "stand": "Booth",
      "zoomIn": "Zoom in",
      "zoomOut": "Zoom out"
    }
  }
</i18n>

<style lang="scss" scoped>
  .fp {
    background: var(--sf-background);
    border-radius: var(--sf-radius);
    height: 100%;
    min-height: 320px;
    overflow: hidden;
    position: relative;
    width: 100%;
  }

  .fp__svg {
    cursor: grab;
    display: block;
    height: 100%;
    touch-action: none;
    user-select: none;
    -webkit-user-select: none;
    width: 100%;

    &:active {
      cursor: grabbing;
    }
  }

  // Hallenplatte
  // Hallenboden leicht getönt, damit die weißen Stände und die Gänge dazwischen klar lesbar sind
  .fp-slab__top {
    fill: #eef1f6;
  }

  .fp-slab__side--front {
    fill: #e3e8ef;
  }

  .fp-slab__side--right {
    fill: #cfd6e0;
  }

  .fp-shadow {
    fill: rgba(17, 24, 39, .07);
  }

  // Blöcke: drei Flächen, drei Töne
  .fp-block {
    outline: none;

    .fp-face--top { fill: #ffffff; }
    .fp-face--front { fill: #e3e8ef; }
    .fp-face--right { fill: #cfd6e0; }
  }

  // Außenwände: Anthrazit, hinten hoch, vorn niedrig
  .fp-block--wall {
    .fp-face--top { fill: #3a4150; }
    .fp-face--front { fill: #2a303c; }
    .fp-face--right { fill: #1f242e; }
  }

  // Innenwände: dünn, dezent, nur wenig höher als die Stände
  .fp-block--thin,
  .fp-block--thick {
    .fp-face--top { fill: #aab2bf; }
    .fp-face--front { fill: #8f98a6; }
    .fp-face--right { fill: #7a8392; }
  }

  .fp-block--empty {
    .fp-face--top { fill: #fafbfd; }
    .fp-face--front { fill: #e3e8ef; }
    .fp-face--right { fill: #cfd6e0; }

    .fp-number {
      fill: var(--sf-text-muted);
    }
  }

  .fp-block--sagasser {
    .fp-face--top { fill: #e4ecf7; }
    .fp-face--front { fill: #c8d7ec; }
    .fp-face--right { fill: #aec3e0; }

    .fp-label {
      fill: var(--sf-primary);
    }
  }

  .fp-block--service {
    .fp-face--top { fill: #e9ecf1; }
    .fp-face--front { fill: #d8dde6; }
    .fp-face--right { fill: #c9cfda; }

    .fp-label {
      fill: var(--sf-text-secondary);
      font-size: 5.5px;
    }
  }

  .fp-block--interactive {
    cursor: pointer;
  }

  // Hover/Fokus/Auswahl: deckende Farben (keine Transparenz, sonst scheint der Aufbau durch)
  .fp-block--interactive.fp-block--hover,
  .fp-block--interactive:focus-visible {
    .fp-face--top { fill: #fde7e8; }
    .fp-face--front { fill: #f4c7ca; }
    .fp-face--right { fill: #e9a9ad; }
  }

  .fp-block--selected {
    .fp-face--top { fill: #fde7e8; }
    .fp-face--front { fill: #f4c7ca; }
    .fp-face--right { fill: #e9a9ad; }
  }

  .fp-block--conflict {
    .fp-face--top { fill: #fde7e8; }
    .fp-face--front { fill: #f4c7ca; }
    .fp-face--right { fill: #e9a9ad; }
  }

  .fp-number {
    fill: var(--sf-text);
    font-size: 8.5px;
    font-weight: 700;
    pointer-events: none;
  }

  .fp-label {
    font-size: 6.5px;
    font-weight: 600;
    pointer-events: none;
  }

  .fp-label__number {
    font-size: 7.5px;
    font-weight: 700;
  }

  // Hervorhebung
  .fp-highlight polygon {
    animation: fp-pulse 1.1s ease-in-out infinite;
    fill: none;
    pointer-events: none;
    stroke: var(--sf-accent);
    stroke-linejoin: round;
    stroke-width: 2.5px;
    vector-effect: non-scaling-stroke;
  }

  @keyframes fp-pulse {
    0%, 100% { stroke-opacity: 1; }
    50% { stroke-opacity: .25; }
  }

  // Eingang
  .fp-entrance {
    pointer-events: none;

    line {
      stroke: var(--sf-primary);
      stroke-dasharray: 1.5 1.5;
      stroke-width: .8px;
    }

    rect {
      fill: var(--sf-primary);
    }

    text {
      fill: #ffffff;
      font-size: 6px;
      font-weight: 700;
      letter-spacing: .02em;
    }
  }

  // Schilder
  .fp-signs {
    opacity: 0;
    pointer-events: none;
    transition: opacity .2s ease;

    &--visible {
      opacity: 1;
      pointer-events: auto;
    }
  }

  .fp-sign {
    cursor: pointer;
  }

  .fp-sign__card {
    fill: #ffffff;
    filter: drop-shadow(0 .6px 1px rgba(17, 24, 39, .18));
    stroke: var(--sf-border);
    stroke-width: .3px;
  }

  .fp-sign--dark .fp-sign__card {
    fill: #1f2937;
    stroke: #1f2937;
  }

  .fp-sign__name {
    fill: var(--sf-text);
    font-size: 2.9px;
    font-weight: 600;
  }

  .fp-sign--dark .fp-sign__name {
    fill: #ffffff;
  }

  .fp-sign__badge {
    fill: var(--sf-text);
  }

  .fp-sign__badge-text {
    fill: #ffffff;
    font-size: 3.1px;
    font-weight: 700;
    pointer-events: none;
  }

  .fp-sign--dark .fp-sign__badge {
    fill: #ffffff;
  }

  .fp-sign--dark .fp-sign__badge-text {
    fill: var(--sf-text);
  }

  // Bedienelemente
  .fp__controls {
    background: var(--sf-surface);
    border: 1px solid var(--sf-border);
    border-radius: var(--sf-radius);
    bottom: 12px;
    box-shadow: var(--sf-shadow-popover);
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 4px;
    position: absolute;
    right: 12px;
  }

  .fp__tooltip {
    background: var(--sf-text);
    border-radius: var(--sf-radius-sm);
    color: #ffffff;
    font-size: .8125rem;
    line-height: 1.3;
    max-width: 240px;
    padding: 6px 10px;
    pointer-events: none;
    position: absolute;
    transform: translate(-50%, calc(-100% - 10px));
    white-space: nowrap;
    z-index: 2;
  }

  .fp__tooltip-title {
    font-weight: 600;
  }

  .fp__tooltip-subtitle {
    opacity: .8;
  }
</style>
